import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  type FC,
  type ReactNode,
} from 'react'
import useConfirmation from '@/hooks/use-confirmation'
import useTranslate from '@/hooks/use-translate'

/**
 * Garde des modifications non enregistrées dans Paramètres.
 *
 * L'app tourne sous `BrowserRouter` : pas de `useBlocker` pour intercepter
 * une navigation. Ce contexte couvre donc les sorties que Paramètres maîtrise
 * (menu de la sidebar, onglets d'une page) : elles passent par `confirmLeave`.
 * Fermeture ou rechargement de l'onglet : `beforeunload`. Un lien hors de
 * Paramètres ou le bouton Précédent du navigateur ne sont pas interceptés.
 */
type Registration = { dirty: boolean; onDiscard?: () => void }

type SettingsUnsavedChangesContextValue = {
  register: (id: string, registration: Registration) => void
  unregister: (id: string) => void
  confirmLeave: (leave: () => void) => void
}

const SettingsUnsavedChangesContext =
  createContext<SettingsUnsavedChangesContextValue | null>(null)

export const SettingsUnsavedChangesProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslate('settings')
  const [confirm, ConfirmDialog] = useConfirmation()
  const registrations = useRef(new Map<string, Registration>())

  const register = useCallback((id: string, registration: Registration) => {
    registrations.current.set(id, registration)
  }, [])

  const unregister = useCallback((id: string) => {
    registrations.current.delete(id)
  }, [])

  const confirmLeave = useCallback(
    (leave: () => void) => {
      const dirty = [...registrations.current.values()].filter((r) => r.dirty)
      if (dirty.length === 0) {
        leave()
        return
      }
      void confirm(
        () => {
          dirty.forEach((r) => r.onDiscard?.())
          leave()
        },
        {
          title: t('settingsLeaveUnsavedTitle'),
          message: t('settingsLeaveUnsavedMessage'),
          confirmLabel: t('settingsLeaveUnsavedConfirm'),
          destructive: true,
          // Quitter n'est pas une suppression : un clic suffit.
          holdToConfirm: false,
        }
      )
    },
    [confirm, t]
  )

  const value = useMemo(
    () => ({ register, unregister, confirmLeave }),
    [register, unregister, confirmLeave]
  )

  return (
    <SettingsUnsavedChangesContext.Provider value={value}>
      {children}
      <ConfirmDialog />
    </SettingsUnsavedChangesContext.Provider>
  )
}

/**
 * Signale des modifications non enregistrées : confirmation avant de quitter
 * par la sidebar ou un onglet, et avertissement du navigateur à la fermeture.
 * `onDiscard` sert quand l'état survit au démontage (formulaire tenu par le
 * parent) : il est appelé si l'utilisateur confirme qu'il quitte.
 */
export const useUnsavedChangesGuard = (
  dirty: boolean,
  onDiscard?: () => void
) => {
  const context = useContext(SettingsUnsavedChangesContext)
  const id = useId()

  useEffect(() => {
    if (!context) return undefined
    context.register(id, { dirty, onDiscard })
    return () => context.unregister(id)
  }, [context, id, dirty, onDiscard])

  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])
}

/** Passe une sortie (navigation, changement d'onglet) par la confirmation. */
export const useConfirmLeave = () => {
  const context = useContext(SettingsUnsavedChangesContext)
  return context?.confirmLeave ?? runNow
}

const runNow = (leave: () => void) => leave()
